// src/pages/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LandingPage.css'; 

const LandingPage = () => {
  // State for mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // State for active feature
  const [activeFeature, setActiveFeature] = useState('resume-optimization');
  // State for active tabs in each capability group
  const [activeTabs, setActiveTabs] = useState({
    'resume-optimization': 'identify',
    'version-management': 'parsing',
    'job-evaluation': 'scan',
    'tracking': 'track'
  });
  // Refs for scroll animation
  const headerRef = useRef(null);
  const observerRef = useRef(null);
  const heroRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: null, y: null });
  const PAPER_CENTERS = [
    [20, 12], [79, 38], [15, 60], [77, 28], [82, 72], [17, 19], [22, 45], [74, 75], [72, 8], [77, 62]
  ];

  // Content maps for tabs
  const contentMap = {
    identify: {
      title: "Discover your Strengths",
      description: "Resumemo helps to know yourself better by extracting and summerizing key points from your resume.",
      content: (
        <div className="use-case-card">
          <div className="image-container">
            <img src="/assets/Strength.png" alt="Image 1" className="img-1" />
            <img src="/assets/JobFit.png" alt="Image 2" className="img-2" />
          </div>
        </div>
      )
    },
    massage: {
      title: "Resume Massage",
      description: "Repurpose your resume for adjacent roles with one click.",
      content: (
        <div className="use-case-card">
          <div className="image-container">
            <img src="/assets/Massage.png" alt="Experience Alignment" className="img-1" />
            <img src="/assets/Massage2.png" alt="Job Fit Visualization" className="img-2" />
          </div>
        </div>
      )
    },
    coverLetter: {
      title: "Generating Cover Letter for the specific job",
      description: "Resumemo assists you to automate generating the cover letter based on your resume and the specific job description.",
      content: (
        <div className="use-case-card">
          <div className="use-case-video">
            <video src="/assets/Short_CoverLetter.mp4" controls autoPlay muted loop playsInline></video>
          </div>
        </div>
      )
    },
    parsing: {
      title: "Intelligent Resume Parsing",
      description: "Resumemo supports multiple document types and automatically scans and parses information, including skills, experience, and education.",
      content: (
        <div className="use-case-card">
          <div className="use-case-video">
            <video src="/assets/Short_Parse.mp4" controls autoPlay muted loop playsInline></video>
          </div>
        </div>
      )
    },
    version: {
      title: "Resume Version Management",
      description: "Resumemo helps you manage multiple versions of your resume tailored to different job applications.",
      content: (
        <ul className="capability-list">
          <li>Create and compare resume versions</li>
          <li>Organize resumes by job role</li>
          <li>Track changes across versions</li>
          <li>Smart version suggestions</li>
        </ul>
      )
    },
    AI: {
      title: "AI Resume Polish",
      description: "Use Resumemo's AI to enhance your resume with improved grammar, phrasing, and tone.",
      content: (
        <ul className="capability-list">
          <li>Grammar and spell check</li>
          <li>Professional tone adjustments</li>
          <li>Bullet point optimization</li>
          <li>Job-specific tailoring</li>
        </ul>
      )
    },
    format: {
      title: "Smart Formatting",
      description: "Resumemo ensures your resume looks professional and consistent with clean templates and layouts.",
      content: (
        <ul className="capability-list">
          <li>Modern and ATS-friendly templates</li>
          <li>Automatic layout adjustments</li>
          <li>Consistent font and spacing</li>
          <li>Download in multiple formats</li>
        </ul>
      )
    },
    scan: {
      title: "Quick Job Save",
      description: "Resumemo enables you to save your favorate job and relevant description just in one click.",
      content: (
        <div className="use-case-card">
          <div className="use-case-video">
            <video src="/assets/Short_Linkedin_Save.mp4" controls autoPlay muted loop playsInline></video>
          </div>
        </div>
      )
    },
    evaluation: {
      title: "Instantly know if the job fits you",
      description: "Resumemo checks your job match. Fast and easy",
      content: (
        <div className="use-case-card">
          <div className="image-container">
            <img src="/assets/Job_fit.png" alt="Experience Alignment" className="img" />
          </div>
        </div>
      )
    },
    gap: {
      title: "Tailor Your Resume",
      description: "Resumemo rewrites your resume to match any job description—instantly.",
      content: (
        <div className="use-case-card">
          <div className="image-container">
            <img src="/assets/Job_Tailored_RV.png" alt="Experience Alignment" className="img" />
          </div>
        </div>
      )
    },
    track: {
      title: "Track your Application Systematically",
      description: "Resumemo supports multiple document's type, scan and parse the information, skills, experience and education automatically.",
      content: (
        <div className="use-case-card">
          <div className="use-case-video">
            <video src="/assets/ParsingResume.mp4" controls autoPlay muted loop playsInline></video>
          </div>
        </div>
      )
    },
    'follow-up': {
      title: "Follow-up Reminders",
      description: "Never miss a follow-up with timely reminders and email templates.",
      content: (
        <ul className="capability-list">
          <li>Automated reminder system</li>
          <li>Customizable follow-up templates</li>
          <li>Scheduling assistant</li>
          <li>Status tracking</li>
        </ul>
      )
    },
    interviews: {
      title: "Mock Interviews",
      description: "Practice with AI-powered mock interviews tailored to specific job roles.",
      content: (
        <ul className="capability-list">
          <li>Role-specific questions</li>
          <li>Real-time feedback</li>
          <li>Interview recording and analysis</li>
          <li>Response suggestions</li>
        </ul>
      )
    }
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Feature toggle
  const handleFeatureClick = (feature) => {
    setActiveFeature(feature);
  };

  // Tab toggle with proper state management
  const handleTabClick = (group, tabId) => {
    setActiveTabs(prevTabs => ({
      ...prevTabs,
      [group]: tabId
    }));
  };

  // Smooth scroll
  const scrollToSection = (sectionId, e) => {
    e.preventDefault();
    const section = document.getElementById(sectionId);
    if (section) {
      const headerHeight = headerRef.current ? headerRef.current.offsetHeight : 0;
      const sectionTop = section.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({
        top: sectionTop,
        behavior: 'smooth'
      });
    }
  };

  // Safe feather icons initialization
  const initFeatherIcons = () => {
    if (window.feather && typeof window.feather.replace === 'function') {
      try {
        // Only replace icons that haven't been replaced yet
        const icons = document.querySelectorAll('[data-feather]:not(.feather-replaced)');
        icons.forEach(icon => {
          icon.classList.add('feather-replaced');
        });
        window.feather.replace();
      } catch (error) {
        console.warn('Feather icons initialization failed:', error);
      }
    }
  };

  // Initialize scroll animations
  const initScrollAnimation = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target) {
          try {
            entry.target.classList.add('visible');
            if (observerRef.current) {
              observerRef.current.unobserve(entry.target);
            }
          } catch (error) {
            console.warn('Animation observer error:', error);
          }
        }
      });
    }, { threshold: 0.2 });

    // Observe elements that should animate on scroll
    const elements = document.querySelectorAll('.feature-card, .step, .use-case-card');
    elements.forEach(el => {
      if (el) {
        try {
          el.classList.add('fade-in');
          if (observerRef.current) {
            observerRef.current.observe(el);
          }
        } catch (error) {
          console.warn('Element observation error:', error);
        }
      }
    });
  };

  // Initialize Feather icons and scroll animations on mount
  useEffect(() => {
    const featherTimeout = setTimeout(initFeatherIcons, 100);
    const animationTimeout = setTimeout(initScrollAnimation, 200);

    // Sticky header implementation
    let lastScrollTop = 0;
    const handleScroll = () => {
      if (!headerRef.current) return;
      
      try {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        if (scrollTop > 0) {
          headerRef.current.classList.add('scrolled');
        } else {
          headerRef.current.classList.remove('scrolled');
        }

        // Hide header when scrolling down, show when scrolling up
        if (scrollTop > lastScrollTop && scrollTop > 200) {
          headerRef.current.style.transform = 'translateY(-100%)';
        } else {
          headerRef.current.style.transform = 'translateY(0)';
        }
        
        lastScrollTop = scrollTop;
      } catch (error) {
        console.warn('Scroll handler error:', error);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup function
    return () => {
      clearTimeout(featherTimeout);
      clearTimeout(animationTimeout);
      window.removeEventListener('scroll', handleScroll);
      
      if (observerRef.current) {
        try {
          observerRef.current.disconnect();
        } catch (error) {
          console.warn('Observer cleanup error:', error);
        }
      }
    };
  }, []); // Empty dependency array to run only once

  // Re-initialize feather icons when content changes (more conservative approach)
  useEffect(() => {
    const reinitFeather = setTimeout(() => {
      initFeatherIcons();
    }, 50);

    return () => clearTimeout(reinitFeather);
  }, [activeFeature]); // Only reinitialize when activeFeature changes, not activeTabs

  // Mouse tracking for paper attraction
  const handleHeroMouseMove = (e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  const handleHeroMouseLeave = () => setMousePos({ x: null, y: null });

  const getPaperOffset = (index) => {
    if (mousePos.x == null || mousePos.y == null || !heroRef.current) return { x: 0, y: 0 };
    const rect = heroRef.current.getBoundingClientRect();
    const [cxPercent, cyPercent] = PAPER_CENTERS[index];
    const paperCenterX = (cxPercent / 100) * rect.width;
    const paperCenterY = (cyPercent / 100) * rect.height;
    const dx = (mousePos.x - paperCenterX) * 0.06;
    const dy = (mousePos.y - paperCenterY) * 0.06;
    return { x: dx, y: dy };
  };

  return (
    <div className="landing-page">
      <header className="header" ref={headerRef}>
        <div className="container">
          <nav className="navbar">
            <div className="logo">
              <img src="/logo.svg" alt="Resumemo" style={{height: '40px', marginRight: '8px', verticalAlign: 'middle'}} />
              <h1>Resumemo</h1>
            </div>
            <ul className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}>
              <li><a href="#use-cases" onClick={(e) => scrollToSection('use-cases', e)}>Audience</a></li>
              <li><a href="#features" onClick={(e) => scrollToSection('features', e)}>Features</a></li>
              <li><a href="#capabilities" onClick={(e) => scrollToSection('capabilities', e)}>Capabilities</a></li>
              <li><a href="#about" onClick={(e) => scrollToSection('about', e)}>About</a></li>
              <li><a href="#team" onClick={(e) => scrollToSection('team', e)}>Team</a></li>
            </ul>
            <div className="cta-button">
              <Link to="/login" className="btn btn-secondary" style={{ marginRight: '8px' }}>Sign In</Link>
              <Link to="/free" className="btn btn-primary">Try for Free</Link>
            </div>
            <div className="mobile-menu-btn" onClick={toggleMobileMenu}>
              <i data-feather={mobileMenuOpen ? "x" : "menu"}></i>
            </div>
          </nav>
        </div>
      </header>

      <section className="hero" ref={heroRef} onMouseMove={handleHeroMouseMove} onMouseLeave={handleHeroMouseLeave}>
        <div className="hero-bg-decor" aria-hidden="true">
          {[...Array(10)].map((_, i) => {
            const offset = getPaperOffset(i);
            return (
              <div
                key={i}
                className={`paper-wrapper paper-wrapper-${i + 1}`}
                style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
              >
                <div className="paper" />
              </div>
            );
          })}
        </div>
        <div className="container">
          <div className="hero-content">

            <h1>
              <span className="hero-title-highlight">Apply smarter</span>
              <span className="hero-title-line">Not harder</span>
            </h1>
            <p className="hero-subtitle">Resumemo tracks your applications, optimizes resumes, and reveals why recruiters aren&apos;t responding.</p>
            <div className="hero-cta-group">
              <Link to="/free" className="btn btn-primary btn-hero">
                Try for Free
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </Link>
              <Link to="/register" className="btn btn-secondary btn-hero">Sign Up</Link>
              <Link to="/login" className="btn btn-secondary btn-hero">Sign In</Link>
              <a href="#features" onClick={(e) => scrollToSection('features', e)} className="btn btn-secondary btn-hero">See How It Works</a>
              <p className="hero-cta-note">Get a free resume diagnosis in 60 seconds — no credit card required</p>
            </div>

          </div>
        </div>
      </section>


      <section className="intro-video">
        <div className="container">
          <div className="responsive-video">
          <iframe width="560" height="315" src="https://www.youtube.com/embed/9IFNQl2TlwI?si=v1ksgDkBws_DF9AK" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
          </div>
        </div>
      </section>

      <section id="use-cases" className="use-cases">
        <div className="container">
          <div className="section-header">
            <h2>Who is Resumemo Built For?</h2>
            <p>Whether you&apos;re starting from scratch, tailoring applications, or tracking offers—Resumemo adapts to your workflow.</p>
          </div>
          
          <div className="use-cases-grid">
            <div className="use-case-card">
              <div className="use-case-image">
                <img src="/assets/Build_from_Scrath_V.png" alt="Build Resume from Scratch" />
              </div>
              <div className="use-case-content">
                <h3>Quick Starters</h3>
                <p>Need a professional resume and cover letter fast? Resumemo generates ATS-ready documents from scratch—ready to download as PDF in minutes.</p>
              </div>
            </div>

            <div className="use-case-card">
              <div className="use-case-image">
                <img src="/assets/Job_tailors_V.png" alt="Tailor Resume to Job" />
              </div>
              <div className="use-case-content">
                <h3>Job Tailorers</h3>
                <p>Customize your resume for every job description with AI-powered suggestions that align your skills and experience to each role's needs.</p>
              </div>
            </div>

            <div className="use-case-card">
              <div className="use-case-image">
                <img src="/assets/Organizer_v.png" alt="Track Applications" />
              </div>
              <div className="use-case-content">
                <h3>Organized Trackers</h3>
                <p>Keep every resume version, job description, and application status neatly tracked. Stay on top of your job hunt effortlessly.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <h2>Key Features</h2>
            <p>Discover why Resumemo is your ultimate job application assistant.</p>
          </div>
          <div className="features-grid">
            <div 
              className={`feature-card ${activeFeature === 'resume-optimization' ? 'active' : ''}`} 
              data-feature="resume-optimization"
              onClick={() => handleFeatureClick('resume-optimization')}
            >
              <div className="feature-icon"><i data-feather="tool"></i></div>
              <h3>AI-Powered Resume Optimization</h3>
            </div>
            <div 
              className={`feature-card ${activeFeature === 'version-management' ? 'active' : ''}`} 
              data-feature="version-management"
              onClick={() => handleFeatureClick('version-management')}
            >
              <div className="feature-icon"><i data-feather="layers"></i></div>
              <h3>Resume Version Management</h3>
            </div>
            <div 
              className={`feature-card ${activeFeature === 'job-evaluation' ? 'active' : ''}`} 
              data-feature="job-evaluation"
              onClick={() => handleFeatureClick('job-evaluation')}
            >
              <div className="feature-icon"><i data-feather="check-circle"></i></div>
              <h3>Job Evaluation and Easy Apply</h3>
            </div>
            <div 
              className={`feature-card ${activeFeature === 'tracking' ? 'active' : ''}`} 
              data-feature="tracking"
              onClick={() => handleFeatureClick('tracking')}
            >
              <div className="feature-icon"><i data-feather="eye"></i></div>
              <h3>Applications Tracking</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Capability groups with conditional rendering based on active feature */}
      <div className="capability-group" style={{ display: activeFeature === 'resume-optimization' ? 'block' : 'none' }}>
        <section id="capabilities" className="capabilities">
          <div className="container">
            <div className="section-header">
              <h2>Our Capabilities</h2>
              <p>AI-Powered Resume Optimization</p>
            </div>
            <div className="capabilities-wrapper">
              <div className="capability-tabs">
                <button 
                  className={`tab-btn ${activeTabs['resume-optimization'] === 'identify' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('resume-optimization', 'identify')}
                >
                  Discover your Strengths
                </button>
                <button 
                  className={`tab-btn ${activeTabs['resume-optimization'] === 'massage' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('resume-optimization', 'massage')}
                >
                  Massage your Resume
                </button>
                <button 
                  className={`tab-btn ${activeTabs['resume-optimization'] === 'coverLetter' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('resume-optimization', 'coverLetter')}
                >
                  Cover Letter Generation
                </button>
              </div>


              <div className="capability-content">
                <div className="tab-content active">
                  <h3>{contentMap[activeTabs['resume-optimization']].title}</h3>
                  <p>{contentMap[activeTabs['resume-optimization']].description}</p>
                  {contentMap[activeTabs['resume-optimization']].content}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="capability-group" style={{ display: activeFeature === 'version-management' ? 'block' : 'none' }}>
        <section id="capabilities2" className="capabilities">
          <div className="container">
            <div className="section-header">
              <h2>Capabilities</h2>
              <p>Resume Version Management</p>
            </div>
            <div className="capabilities-wrapper">
              <div className="capability-tabs">
                <button 
                  className={`tab-btn ${activeTabs['version-management'] === 'parsing' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('version-management', 'parsing')}
                >
                  Intelligent Parsing
                </button>
                <button 
                  className={`tab-btn ${activeTabs['version-management'] === 'version' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('version-management', 'version')}
                >
                  Version Management
                </button>
                <button 
                  className={`tab-btn ${activeTabs['version-management'] === 'AI' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('version-management', 'AI')}
                >
                  AI Polish
                </button>
                <button 
                  className={`tab-btn ${activeTabs['version-management'] === 'format' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('version-management', 'format')}
                >
                  Formatting
                </button>
              </div>
              <div className="capability-content">
                <div className="tab-content active">
                  <h3>{contentMap[activeTabs['version-management']].title}</h3>
                  <p>{contentMap[activeTabs['version-management']].description}</p>
                  {contentMap[activeTabs['version-management']].content}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="capability-group" style={{ display: activeFeature === 'job-evaluation' ? 'block' : 'none' }}>
        <section id="capabilities3" className="capabilities">
          <div className="container">
            <div className="section-header">
              <h2>Capabilities</h2>
              <p>Job Evaluation and Easy Apply</p>
            </div>
            <div className="capabilities-wrapper">
              <div className="capability-tabs">
                <button 
                  className={`tab-btn ${activeTabs['job-evaluation'] === 'scan' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('job-evaluation', 'scan')}
                >
                  Quick Job Save
                </button>
                <button 
                  className={`tab-btn ${activeTabs['job-evaluation'] === 'evaluation' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('job-evaluation', 'evaluation')}
                >
                  Job Matching Evaluation
                </button>
                <button 
                  className={`tab-btn ${activeTabs['job-evaluation'] === 'gap' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('job-evaluation', 'gap')}
                >
                  Fill Skill Gap
                </button>
              </div>
              <div className="capability-content">
                <div className="tab-content active">
                  <h3>
                    {contentMap[activeTabs['job-evaluation']].title}
                    {activeTabs['job-evaluation'] === 'gap' && <i data-feather="star"></i>}
                  </h3>
                  <p>{contentMap[activeTabs['job-evaluation']].description}</p>
                  {contentMap[activeTabs['job-evaluation']].content}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="capability-group" style={{ display: activeFeature === 'tracking' ? 'block' : 'none' }}>
        <section id="capabilities4" className="capabilities">
          <div className="container">
            <div className="section-header">
              <h2>Capabilities</h2>
              <p>Applications Tracking</p>
            </div>
            <div className="capabilities-wrapper">
              <div className="capability-tabs">
                <button 
                  className={`tab-btn ${activeTabs['tracking'] === 'track' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('tracking', 'track')}
                >
                  Track Dashboard
                </button>
                <button 
                  className={`tab-btn ${activeTabs['tracking'] === 'follow-up' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('tracking', 'follow-up')}
                >
                  Follow-up Reminders
                </button>
                <button 
                  className={`tab-btn ${activeTabs['tracking'] === 'interviews' ? 'active' : ''}`} 
                  onClick={() => handleTabClick('tracking', 'interviews')}
                >
                  Mock Interviews
                </button>
              </div>
              <div className="capability-content">
                <div className="tab-content active">
                  <h3>{contentMap[activeTabs['tracking']].title}</h3>
                  <p>{contentMap[activeTabs['tracking']].description}</p>
                  {contentMap[activeTabs['tracking']].content}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section id="get-started" className="get-started">
        <div className="container">
          <div className="section-header">
            <h2>Get Started with Resumemo</h2>
            <p>Create an account and complete your profile so Resumemo can personalize your experience.</p>
          </div>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Sign Up</h3>
              <p>Create your account to access Resumemo</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Upload Your Resume</h3>
              <p>Tell Resumemo who you are</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Navigate to Your Dream Job</h3>
              <p>Tell Resumemo what is your goal</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Leave it to Resumemo!</h3>
              <p>Let Resumemo help you complete the job application</p>
            </div>
          </div>
        </div>
        <div className="cta-container">
          <Link to="/free" className="btn btn-primary">Start Now — It&apos;s Free</Link>
          <p className="cta-note">No credit card required • Get your diagnosis in 60 seconds</p>
        </div>
      </section>

      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <h2>About Resumemo</h2>
            <p>Resumemo is a smart AI assistant built to help users craft standout resumes, optimize job applications, and navigate career growth with confidence. Designed to understand your goals and background, Resumemo combines advanced language capabilities with tailored tools to deliver expert guidance through natural, conversational interactions.</p>
            <p>Whether you're refining your resume, preparing for interviews, or exploring new career paths, Resumemo makes professional development feel intuitive, efficient, and personalized.</p>
          </div>
        </div>
      </section>

      <section id="team" className="team">
        <div className="container">
          <div className="section-header">
            <h2>Meet Our Team</h2>
            <p>The builders behind Resumemo</p>
          </div>
          <div className="team-grid">
            <div className="team-member">
              <div className="member-photo">
                <img src="/assets/xixi.png" alt="Xixi and Qi - Resumemo Founders" className="w-full h-auto rounded-3xl" />
              </div>
              <div className="member-info">
                <h3>Xixi Zhou</h3>
                <p className="member-role">Co-founder</p>
                <p className="member-description">👩‍💻 Product · 🎨 Design · ⚙️ Ops · 📣 Marketing</p>
                <div className="member-social">
                  <a href="https://www.linkedin.com/in/xixi-zhou-807476a1/" aria-label="LinkedIn"><i data-feather="linkedin"></i></a>
                </div>
              </div>
            </div>
            <div className="team-member">
              <div className="member-photo">
                <img src="/assets/qi.png" alt="Xixi and Qi - Resumemo Founders" className="w-full h-auto rounded-3xl" />
              </div>
              <div className="member-info">
                <h3>Qi Zhao</h3>
                <p className="member-role">Co-founder</p>
                <p className="member-description">💻 Fullstack · 🧠 Infra · 🤖 Automation</p>
                <div className="member-social">
                  <a href="https://www.linkedin.com/in/qiz216/" aria-label="LinkedIn"><i data-feather="linkedin"></i></a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="trust-section">
        <div className="container">
          <div className="trust-grid">
            <div className="trust-item">
              <div className="trust-icon"><i data-feather="shield"></i></div>
              <h3>Your Data is Secure</h3>
              <p>We use industry-standard encryption and never share your personal information.</p>
            </div>
            <div className="trust-item">
              <div className="trust-icon"><i data-feather="lock"></i></div>
              <h3>Privacy First</h3>
              <p>Your resumes and applications are never shared with third parties.</p>
            </div>
            <div className="trust-item">
              <div className="trust-icon"><i data-feather="help-circle"></i></div>
              <h3>Need Help?</h3>
              <p>Contact us anytime at <a href="mailto:support@myresumemo.com">support@myresumemo.com</a></p>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="/logo.svg" alt="Resumemo" className="h-8 w-auto mb-2" style={{ filter: 'brightness(0) invert(1)' }} />
              <h2>Resumemo</h2>
              <p>Your all-in-one job hunting companion.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h3>Product</h3>
                <ul>
                  <li><a href="#use-cases" onClick={(e) => scrollToSection('use-cases', e)}>Audience</a></li>
                  <li><a href="#features" onClick={(e) => scrollToSection('features', e)}>Features</a></li>
                  <li><a href="#capabilities" onClick={(e) => scrollToSection('capabilities', e)}>Capabilities</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h3>Company</h3>
                <ul>
                  <li><a href="#about" onClick={(e) => scrollToSection('about', e)}>About</a></li>
                  <li><a href="#team" onClick={(e) => scrollToSection('team', e)}>Team</a></li>
                  <li><a href="#careers">Careers</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="legal-links">
            <Link to="/team">Our Team</Link>
            <span className="separator">·</span>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <span className="separator">·</span>
              <Link to="/terms">Terms of Service</Link>
            </div>
            <p>&copy; 2026 Resumemo AI. All rights reserved.</p>
            <div className="social-links">
              {/* Footer with navigation               
              <a href="#" aria-label="Twitter"><i data-feather="twitter"></i></a>
              <a href="#" aria-label="GitHub"><i data-feather="github"></i></a>

 */}
              <a href="https://www.linkedin.com/company/myresumemo/" aria-label="LinkedIn"><i data-feather="linkedin"></i></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;